import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Tooltip,
  IconButton,
  Popover,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MonitorIcon from '@mui/icons-material/Monitor';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LogoutIcon from '@mui/icons-material/Logout';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import TvIcon from '@mui/icons-material/Tv';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { useAuth } from '../context/AuthContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#090a14',
      paper: '#121422',
    },
    primary: {
      main: '#ff2d9b',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Segoe UI", sans-serif',
  },
});

export default function MobileConsoleNotice({ onBypass }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [anchorUserMenu, setAnchorUserMenu] = useState(null);

  const handleGoToRemote = () => {
    navigate('/remote');
  };

  const handleBypass = () => {
    try {
      sessionStorage.setItem('garaoke_bypass_mobile_notice', 'true');
    } catch (e) {}
    if (typeof onBypass === 'function') {
      onBypass();
    }
  };

  const handleLogout = () => {
    if (typeof logout === 'function') {
      logout();
    } else {
      setUser(null);
    }
    navigate('/');
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'radial-gradient(circle at 50% 15%, rgba(180, 77, 255, 0.15) 0%, rgba(9, 10, 20, 0.98) 75%), #090a14',
          color: '#ffffff',
          px: 2.5,
          py: 3,
          boxSizing: 'border-box',
        }}
      >
        {/* Header Superior */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '480px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 10px rgba(255, 45, 155, 0.4)',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: '#fff' }}>G</Typography>
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: '0.5px' }}>
              GARAOKE
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Avatar interactivo para desplegar submenú */}
            <Tooltip title={user?.displayName || 'Cuenta'}>
              <IconButton
                size="small"
                onClick={(e) => setAnchorUserMenu(e.currentTarget)}
                sx={{
                  p: '2px',
                  background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.6) 0%, rgba(180, 77, 255, 0.6) 100%)',
                  boxShadow: '0 0 10px rgba(255, 45, 155, 0.35)',
                  '&:hover': { transform: 'scale(1.08)' },
                }}
              >
                <Avatar
                  src={user?.photoURL}
                  sx={{ width: 30, height: 30, border: '2px solid #0d0617' }}
                />
              </IconButton>
            </Tooltip>

            {/* Submenú desplegable bajo el avatar */}
            <Popover
              open={Boolean(anchorUserMenu)}
              anchorEl={anchorUserMenu}
              onClose={() => setAnchorUserMenu(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#130924 !important',
                    background: '#130924 !important',
                    border: '1px solid rgba(255, 255, 255, 0.16) !important',
                    borderRadius: '16px !important',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.95), 0 0 20px rgba(180, 77, 255, 0.2) !important',
                    p: 2,
                    minWidth: 260,
                    mt: 1,
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Perfil */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pb: 1.2, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Avatar src={user?.photoURL} sx={{ width: 38, height: 38, border: '2px solid #ff2d9b' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.displayName || 'Usuario Garaoke'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.email || ''}
                    </Typography>
                  </Box>
                </Box>

                {/* Switch de idiomas integrado en submenú */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.2, py: 0.8, borderRadius: '10px', background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.75)', fontWeight: 600 }}>
                    {t('header.language') || 'Idioma'}
                  </Typography>
                  <LanguageSwitcher id="notice-user-menu-language-switcher" />
                </Box>

                {/* Cerrar sesión */}
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<LogoutIcon fontSize="small" />}
                  onClick={() => {
                    setAnchorUserMenu(null);
                    handleLogout();
                  }}
                  sx={{
                    color: '#ff4d6d',
                    borderColor: 'rgba(255, 77, 109, 0.3)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#ff4d6d',
                      background: 'rgba(255, 77, 109, 0.1)',
                    }
                  }}
                >
                  {t('mobileNotice.logout') || 'Cerrar sesión'}
                </Button>
              </Box>
            </Popover>
          </Box>
        </Box>

        {/* Tarjeta Central Informativa */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '480px',
            my: 'auto',
            py: 3,
            px: { xs: 2.5, sm: 3.5 },
            borderRadius: '24px',
            background: 'rgba(18, 8, 32, 0.85)',
            border: '1px solid rgba(180, 77, 255, 0.3)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(255, 45, 155, 0.12)',
            backdropFilter: 'blur(16px)',
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          {/* Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              background: 'rgba(0, 212, 255, 0.12)',
              border: '1px solid rgba(0, 212, 255, 0.35)',
              color: '#00d4ff',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              mb: 2.5,
            }}
          >
            <SmartphoneIcon sx={{ fontSize: 15 }} />
            {t('mobileNotice.badge')}
          </Box>

          {/* Gráfico Visual: Laptop / TV -> Móvil */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 1.5, sm: 2 },
              mb: 3,
            }}
          >
            {/* Pantalla Desktop */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00d4ff',
              }}
            >
              <LaptopMacIcon sx={{ fontSize: 32 }} />
              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255, 255, 255, 0.6)', mt: 0.3, fontWeight: 600 }}>
                PC / TV
              </Typography>
            </Box>

            <ArrowForwardIcon sx={{ color: 'rgba(255, 255, 255, 0.35)', fontSize: 22 }} />

            {/* Smartphone Control Remoto */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.2) 0%, rgba(180, 77, 255, 0.2) 100%)',
                border: '1px solid #ff2d9b',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ff2d9b',
                boxShadow: '0 0 15px rgba(255, 45, 155, 0.3)',
              }}
            >
              <SmartphoneIcon sx={{ fontSize: 30 }} />
              <Typography sx={{ fontSize: '0.62rem', color: '#ff2d9b', mt: 0.3, fontWeight: 700 }}>
                REMOTE
              </Typography>
            </Box>
          </Box>

          {/* Título Principal */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.25rem', sm: '1.45rem' },
              lineHeight: 1.25,
              mb: 1.2,
              letterSpacing: '-0.2px',
            }}
          >
            {t('mobileNotice.title')}
          </Typography>

          {/* Subtítulo claro */}
          <Typography
            sx={{
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.72)',
              lineHeight: 1.45,
              mb: 2.5,
            }}
          >
            {t('mobileNotice.subtitle')}
          </Typography>

          {/* Caja con pasos breves */}
          <Box
            sx={{
              p: 1.8,
              borderRadius: '14px',
              background: 'rgba(0, 0, 0, 0.35)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              textAlign: 'left',
              mb: 3,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#00ffd5', mb: 1, letterSpacing: '0.3px' }}>
              {t('mobileNotice.howItWorks')}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.8)', mb: 0.8, lineHeight: 1.35 }}>
              • {t('mobileNotice.step1')}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.35 }}>
              • {t('mobileNotice.step2')}
            </Typography>
          </Box>

          {/* Botón Principal a Control Remoto */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleGoToRemote}
            startIcon={<SmartphoneIcon />}
            sx={{
              py: 1.4,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)',
              fontWeight: 800,
              fontSize: '0.92rem',
              textTransform: 'none',
              boxShadow: '0 8px 25px rgba(255, 45, 155, 0.45)',
              mb: 1.8,
              '&:hover': {
                background: 'linear-gradient(135deg, #ff45ab 0%, #c466ff 100%)',
                boxShadow: '0 10px 30px rgba(255, 45, 155, 0.6)',
              },
            }}
          >
            {t('mobileNotice.goToRemote')}
          </Button>

          {/* Opción para forzar consola de escritorio */}
          <Typography
            onClick={handleBypass}
            sx={{
              fontSize: '0.74rem',
              color: 'rgba(255, 255, 255, 0.4)',
              cursor: 'pointer',
              textDecoration: 'underline',
              transition: 'color 0.2s ease',
              '&:hover': { color: 'rgba(255, 255, 255, 0.8)' },
            }}
          >
            {t('mobileNotice.openAnyway')}
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ width: '100%', maxWidth: '480px', textAlign: 'center', mt: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.3)' }}>
            Garaoke Live Karaoke Platform
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
